import { useState } from 'react'
import { enablePush, pushEnvironment, notificationPermission } from '../push.js'

/**
 * On iOS, web push requires the app to be installed to the home screen. Asking
 * for permission from a Safari tab silently fails, so we detect that case and
 * give install instructions instead of a button that can't work.
 */
export default function PushBanner() {
  const [state, setState] = useState(() => ({
    env: pushEnvironment(),
    permission: notificationPermission(),
    error: null,
    busy: false,
  }))

  if (state.permission === 'granted') return null

  if (state.env.reason === 'needs-install') {
    return (
      <div className="banner">
        <strong>Turn on alerts</strong>
        <p>
          iOS only delivers push to an installed app. Tap <b>Share</b> → <b>Add to Home Screen</b>,
          then open Wingfinder from your home screen and enable alerts there.
        </p>
      </div>
    )
  }

  if (state.env.reason === 'unsupported') {
    return (
      <div className="banner">
        <strong>Alerts unavailable</strong>
        <p>This browser doesn't support web push. The list below still updates live.</p>
      </div>
    )
  }

  const onClick = async () => {
    setState((s) => ({ ...s, busy: true, error: null }))
    try {
      const result = await enablePush()
      setState((s) => ({
        ...s,
        busy: false,
        permission: notificationPermission(),
        error: result.ok ? null : result.reason,
      }))
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: err.message }))
    }
  }

  return (
    <div className="banner">
      <strong>Turn on alerts</strong>
      <p>Get a push when a new match appears or a tracked bike drops in price.</p>
      <button onClick={onClick} disabled={state.busy}>
        {state.busy ? 'Enabling…' : 'Enable alerts'}
      </button>
      {state.error && (
        <p className="err">
          {state.error === 'denied'
            ? 'Notifications were blocked. Enable them for this app in Settings.'
            : state.error === 'no-vapid-key'
              ? 'No VAPID key configured in this build.'
              : `Could not enable alerts: ${state.error}`}
        </p>
      )}
    </div>
  )
}
